import React, { useState } from 'react';
import { QRDesign, QRExportSettings, QRHealthReport } from '@/types/qr';
import { QRRenderer } from './QRRenderer';
import { QRHealthIndicator } from './QRHealthIndicator';
import { QRTechnicalDetails } from './QRTechnicalDetails';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { downloadQRCode, copyQRImageToClipboard, copyTextToClipboard, shareQRCode } from '@/lib/qr/export';
import {
  Download,
  Copy,
  Share2,
  Star,
  Settings2,
  Check
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';

interface QRPreviewProps {
  rawText: string;
  design: QRDesign;
  health: QRHealthReport;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const QRPreview: React.FC<QRPreviewProps> = ({
  rawText,
  design,
  health,
  isFavorite = false,
  onToggleFavorite
}) => {
  const { showToast } = useToast();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportSettings, setExportSettings] = useState<QRExportSettings>({
    format: 'png',
    size: 1024,
    filename: 'qr-code-studio'
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copiedRecently, setCopiedRecently] = useState(false);

  const handleDownload = async (format?: 'png' | 'svg') => {
    try {
      setIsDownloading(true);
      const settings = format ? { ...exportSettings, format } : exportSettings;
      await downloadQRCode(rawText, design, settings);
      confetti({ particleCount: 30, spread: 50, origin: { y: 0.8 } });
      showToast('success', 'Download Started', `Saved as ${settings.filename}.${settings.format}`);
      setIsExportModalOpen(false);
    } catch (err) {
      console.error(err);
      showToast('error', 'Download Failed', 'Could not export QR code. Try SVG instead.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyImage = async () => {
    setIsCopying(true);
    const success = await copyQRImageToClipboard(rawText, design);
    setIsCopying(false);

    if (success) {
      setCopiedRecently(true);
      setTimeout(() => setCopiedRecently(false), 2500);
      confetti({ particleCount: 20, spread: 40, origin: { y: 0.7 } });
      showToast('success', 'QR Image Copied', 'Ready to paste into documents or chat.');
    } else {
      await copyTextToClipboard(rawText);
      showToast('info', 'Content Copied', 'Copied text string to clipboard.');
    }
  };

  const handleShare = async () => {
    const shared = await shareQRCode(rawText, design, 'My QR Code');
    if (shared) {
      showToast('success', 'Shared successfully');
    }
  };

  const frame = design.frame;
  const hasFrame = frame && frame.type !== 'none';

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Museum Gallery Showcase Tile */}
      <div className="relative w-full rounded-3xl p-8 bg-card border border-border shadow-xs flex flex-col items-center justify-center min-h-[380px] overflow-hidden">
        
        {/* Top Controls: Favorite Star */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5 z-10">
          {onToggleFavorite && (
            <button
              onClick={onToggleFavorite}
              className={`p-2 rounded-full border transition-all cursor-pointer ${
                isFavorite
                  ? 'bg-warning text-warning-foreground border-warning'
                  : 'bg-muted text-muted-foreground hover:text-warning border-transparent'
              }`}
              title={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
            >
              <Star className="h-4 w-4 fill-current" />
            </button>
          )}
        </div>

        {/* Framing Wrapper */}
        <div
          className={`flex flex-col items-center justify-center transition-all duration-300 ${
            hasFrame
              ? 'p-6 rounded-2xl border border-border'
              : ''
          }`}
          style={{
            backgroundColor: hasFrame ? frame.backgroundColor || '#ffffff' : 'transparent'
          }}
        >
          {/* Top Badge Frame */}
          {hasFrame && frame.type === 'top-badge' && (
            <div
              className="px-5 py-1.5 mb-4 rounded-full text-white text-[12px] font-semibold tracking-tight"
              style={{ backgroundColor: frame.textColor || '#0071e3' }}
            >
              {frame.text || 'SCAN ME'}
            </div>
          )}

          {/* QR Canvas Render Resting on Surface with Apple Product Shadow */}
          <div className="p-5 rounded-2xl bg-white product-shadow border border-border flex items-center justify-center">
            <QRRenderer
              data={rawText || 'https://qr.planmaster.in'}
              design={design}
              size={240}
            />
          </div>

          {/* Bottom Badge Frame */}
          {hasFrame && frame.type === 'bottom-badge' && (
            <div
              className="px-6 py-2 mt-4 rounded-full text-white text-[12px] font-semibold tracking-tight active:scale-95 transition-transform"
              style={{ backgroundColor: frame.textColor || '#0071e3' }}
            >
              {frame.text || 'SCAN ME'}
            </div>
          )}

          {/* Polaroid Card Frame */}
          {hasFrame && frame.type === 'card-polaroid' && (
            <div className="mt-4 text-center">
              <p
                className="text-[17px] font-semibold tracking-[-0.374px]"
                style={{ color: frame.textColor || '#1d1d1f' }}
              >
                {frame.text || 'Scan with Camera'}
              </p>
              {frame.subtext && (
                <p className="text-[14px] text-muted-foreground mt-0.5">{frame.subtext}</p>
              )}
            </div>
          )}
        </div>

        {/* Live Scanability Health Pill */}
        <div className="mt-6 w-full max-w-xs">
          <QRHealthIndicator report={health} />
        </div>
      </div>

      {/* Primary Action Buttons */}
      <Button
        variant="primary"
        size="lg"
        onClick={() => handleDownload('png')}
        isLoading={isDownloading}
        leftIcon={<Download className="h-4 w-4" />}
        className="w-full"
      >
        Download PNG
      </Button>

      <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
        <Button
          variant="outline"
          size="md"
          onClick={handleCopyImage}
          isLoading={isCopying}
          leftIcon={copiedRecently ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        >
          {copiedRecently ? 'Copied' : 'Copy Image'}
        </Button>

        <Button
          variant="outline"
          size="md"
          onClick={handleShare}
          leftIcon={<Share2 className="h-4 w-4" />}
        >
          Share
        </Button>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExportModalOpen(true)}
        leftIcon={<Settings2 className="h-3.5 w-3.5" />}
        className="w-full text-[13px] text-muted-foreground hover:text-foreground"
      >
        More export options (SVG, resolution, filename)
      </Button>

      {/* Expandable Technical Specs */}
      <QRTechnicalDetails rawText={rawText} design={design} health={health} />

      {/* Advanced Export Modal */}
      <Modal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Custom Export Settings"
        description="Choose high-resolution print dimensions, file formats, and naming"
      >
        <div className="space-y-4 pt-1">
          <Input
            label="File Name"
            value={exportSettings.filename}
            onChange={(e) => setExportSettings({ ...exportSettings, filename: e.target.value })}
            placeholder="my-company-qr"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Format"
              value={exportSettings.format}
              onChange={(e) =>
                setExportSettings({ ...exportSettings, format: e.target.value as any })
              }
              options={[
                { value: 'png', label: 'PNG (Raster Image)' },
                { value: 'svg', label: 'SVG (Lossless Vector)' },
                { value: 'jpeg', label: 'JPEG (Standard Image)' },
                { value: 'webp', label: 'WebP (High-Compression)' }
              ]}
            />

            <Select
              label="Resolution"
              value={String(exportSettings.size)}
              onChange={(e) =>
                setExportSettings({ ...exportSettings, size: Number(e.target.value) as any })
              }
              options={[
                { value: '256', label: '256 x 256 px (Thumbnail)' },
                { value: '512', label: '512 x 512 px (Standard)' },
                { value: '1024', label: '1024 x 1024 px (HD Print)' },
                { value: '2048', label: '2048 x 2048 px (2K Ultra)' },
                { value: '4096', label: '4096 x 4096 px (4K Billboard)' }
              ]}
            />
          </div>

          <div className="pt-3 border-t border-border flex justify-end gap-2.5">
            <Button variant="ghost" onClick={() => setIsExportModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleDownload()}
              isLoading={isDownloading}
              leftIcon={<Download className="h-4 w-4" />}
            >
              Export Now
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
